import React from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import Toggler from 'components/form/Toggler.component';
import Badge from 'components/generic/Badge.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Tooltip from 'components/generic/Tooltip.component';
import TooltipContentReceptionist from 'components/generic/TooltipContentReceptionist.component';
import StripeAccountVerifierModal from 'components/modal/StripeAccountVerifierModal.component';
import CurrencySign from 'components/price/CurrencySign.component';
import DEPOSIT_TYPE from 'constants/settings/ncobDepositSettings.const';
import S from 'constants/string.const';
import { withTranslation } from 'providers/GILT.provider';
import { withSalonRestriction } from 'providers/SalonBasicInfo.provider';
import { withTheme } from 'providers/Theme.provider';
import { selectCurrentSalonStripeAccount } from 'selectors/currentSalon.selectors';
import { isEffectiveReceptionist } from 'selectors/user';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import logger from 'utils/logger.util';

import BeforeSetupStripeModal from 'settings/online-booking/modals/BeforeSetupStripeModal';
import DepositModal from 'settings/page-elements/setup-nsp-deposit/modals/DepositModal';
import StripeModal from 'settings/page-elements/setup-nsp-deposit/modals/StripeModal';

import CN from './DepositSetup.module.scss';

const L = logger('DepositSetup');

const ROADMAP_LINK = 'http://help.getslick.com/en/articles/4175597-online-deposit-payments';

class DepositSetup extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      depositModal: false,
      paymentFeesModal: false,
      stripeModal: false,
      checked: props.ncob_deposit_settings !== 'null',
    };
  }

  componentDidMount() {
    const { random_key: randomKey, salonId, isFetchingRandomKey, fetchSalonState } = this.props;

    if (!randomKey && !isFetchingRandomKey) {
      L.unignore('componentDidMount()', 'fetchSalonState:', fetchSalonState(fromAnyToString(salonId)));
    }
  }

  handleSaveDeposit = (data) => {
    if (this.props.has_ncob_stripe_account) {
      this.setState({ depositModal: false });
    } else {
      this.setState({ paymentFeesModal: true, depositModal: false }); // ?
    }
    this.props.SetSalonNoshowOrDeposit(data, this.props.salonId);
  };

  onDepositCheckboxClick = () => {
    const { ncob_deposit_settings: deposit, onDepositCheckboxClick } = this.props;

    this.setState({ checked: !this.state.checked });
    if (
      deposit === DEPOSIT_TYPE.fullAmount ||
      deposit === DEPOSIT_TYPE.customAmount ||
      deposit === DEPOSIT_TYPE.halfAmount ||
      deposit === DEPOSIT_TYPE.quarterAmount
    ) {
      onDepositCheckboxClick();
    }

    if (deposit === DEPOSIT_TYPE.nullAmount) this.setState({ depositModal: true });
  };

  closeModal = () => void this.setState({ depositModal: false });
  handleEditDeposit = () => void this.setState({ depositModal: true });
  togglePaymentFeesModal = () => void this.setState({ paymentFeesModal: !this.state.paymentFeesModal });

  render() {
    const { isReceptionist, ncob_deposit_settings: ncobDepositSettings } = this.props;
    const {
      isRestrictedToCriticalServices,
      isRestrictionCheckEnabled,
      enableRestrictionCheck,
      disableRestrictionCheck,
      t,
    } = this.props;
    const { paymentFeesModal, stripeModal } = this.state;

    const renderedCurrentDepositSetting = () => {
      const depositSettings = {
        [DEPOSIT_TYPE.fullAmount]: t('onlineBooking.generalSettings.businessStripeAccount.upfront', { percent: 100 }),
        [DEPOSIT_TYPE.halfAmount]: t('onlineBooking.generalSettings.businessStripeAccount.upfront', { percent: 50 }),
        [DEPOSIT_TYPE.quarterAmount]: t('onlineBooking.generalSettings.businessStripeAccount.upfront', { percent: 25 }),
        [DEPOSIT_TYPE.customAmount]: (
          <>
            <CurrencySign />
            {this.props.ncob_custom_amount} {t('onlineBooking.generalSettings.businessStripeAccount.upfront')}
          </>
        ),
        [DEPOSIT_TYPE.nullAmount]: t('onlineBooking.generalSettings.businessStripeAccount.upfront', { percent: 0 }),
      };

      return (
        <>
          {t('onlineBooking.generalSettings.businessStripeAccount.currentCharge')}{' '}
          {depositSettings[ncobDepositSettings] ||
            t('onlineBooking.generalSettings.businessStripeAccount.upfront', { percent: 0 })}
        </>
      );
    };

    const isDepositEnabled = ncobDepositSettings === DEPOSIT_TYPE.nullAmount;

    const handleSetup = () => {
      if (isRestrictedToCriticalServices) enableRestrictionCheck();
      else this.togglePaymentFeesModal();
    };

    return (
      <div data-bem="DepositSetup">
        <div
          className={cn({
            [CN.component]: true,
            [CN.darkMode]: this.props.isDarkMode,
          })}
        >
          {!this.props.has_ncob_stripe_account ? (
            <>
              <div className={CN.content}>
                <div className={CN.title}>{t('onlineBooking.generalSettings.businessStripeAccount.header2')}</div>
                <div className={CN.text}>{t('onlineBooking.generalSettings.businessStripeAccount.description2')}</div>

                <div className={CN.buttonsContainer}>
                  {isReceptionist ? (
                    <Tooltip variant="slickTop" content={<TooltipContentReceptionist />}>
                      <Button variant="primary">
                        {t('onlineBooking.generalSettings.businessStripeAccount.confirmButton')}
                      </Button>
                    </Tooltip>
                  ) : (
                    <Button onClick={handleSetup} variant="primary">
                      {t('onlineBooking.generalSettings.businessStripeAccount.confirmButton')}
                    </Button>
                  )}

                  <ExternalLink link={ROADMAP_LINK} variant="primary">
                    {t('onlineBooking.generalSettings.needHelp')}
                  </ExternalLink>
                </div>
              </div>
              <div className={CN.imageContainer} />
            </>
          ) : (
            <div className={CN.content}>
              <div className={CN.title}>
                <div className={CN.flex}>
                  {t('onlineBooking.generalSettings.businessStripeAccount.header2')}
                  <div className={CN.gap}>
                    <Badge variant="fine" disabled={isDepositEnabled}>
                      <span className={CN.emojiSize}>{S.moneyBag}</span>
                      {S.space}
                      {renderedCurrentDepositSetting()}
                    </Badge>
                  </div>
                </div>
                {isReceptionist ? (
                  <Tooltip variant="slickTop" content={<TooltipContentReceptionist />}>
                    <Toggler
                      tag="DepositSetup-toggle"
                      selected={this.props.ncob_deposit_settings !== DEPOSIT_TYPE.nullAmount}
                    />
                  </Tooltip>
                ) : (
                  <Toggler
                    tag="DepositSetup-toggle"
                    onClick={this.onDepositCheckboxClick}
                    selected={this.props.ncob_deposit_settings !== DEPOSIT_TYPE.nullAmount}
                  />
                )}
              </div>
              <div className={CN.text}>{t('onlineBooking.generalSettings.businessStripeAccount.description2')}</div>
              <div className={CN.text}>{t('onlineBooking.generalSettings.businessStripeAccount.description3')}</div>
              <div className={CN.negativeGap}>
                {isReceptionist ? (
                  <Tooltip variant="slickTop" content={<TooltipContentReceptionist />}>
                    <Button variant="slim" disabled={isDepositEnabled}>
                      {t('onlineBooking.generalSettings.businessStripeAccount.editDeposit')}
                    </Button>
                  </Tooltip>
                ) : (
                  <>
                    {isDepositEnabled ? (
                      <Tooltip
                        variant="top"
                        width="md"
                        content={t('onlineBooking.generalSettings.businessStripeAccount.tooltip')}
                      >
                        <Button onClick={this.handleEditDeposit} variant="slim" disabled>
                          {t('onlineBooking.generalSettings.businessStripeAccount.editDeposit')}
                        </Button>
                      </Tooltip>
                    ) : (
                      <Button onClick={this.handleEditDeposit} variant="slim">
                        {t('onlineBooking.generalSettings.businessStripeAccount.editDeposit')}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {fromAnyToBool(this.state.depositModal) && (
          <DepositModal
            onSave={this.handleSaveDeposit}
            onClose={this.closeModal}
            ncob_deposit_settings={ncobDepositSettings}
            ncob_custom_amount={this.props.ncob_custom_amount}
          />
        )}

        <BeforeSetupStripeModal isOpen={paymentFeesModal} onClose={this.togglePaymentFeesModal} />

        {stripeModal && (
          <StripeModal>
            <div className="spinner-text">
              {t('onlineBooking.generalSettings.businessStripeAccount.stripeModalText')}
            </div>
          </StripeModal>
        )}

        <StripeAccountVerifierModal
          tag="DepositSetup__restriction-modal"
          enabled={isRestrictionCheckEnabled}
          onCancel={disableRestrictionCheck}
          onClose={disableRestrictionCheck}
        />
      </div>
    );
  }
}

const DepositSetupHoc = connect((state) => ({
  hasStripeAccount: selectCurrentSalonStripeAccount(state),
  isReceptionist: isEffectiveReceptionist(state),
}))(withTranslation()(withTheme()(withSalonRestriction(DepositSetup))));

export default DepositSetupHoc;
